"use client";

import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
} from "react";
// import { motion } from "framer-motion"; // Removed for performance
import { cn } from "../utils/cn";

// ============================================================================
// 1. TYPES & INTERFACES
// ============================================================================

interface AnimationConfig {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  delay?: number;
  duration?: number;
}

interface AnimatedElementProps {
  children: React.ReactNode;
  className?: string;
  config?: AnimationConfig;
  as?: keyof JSX.IntrinsicElements;
}

// ============================================================================
// 2. ANIMATION VARIANTS (DISABLED FOR PERFORMANCE)
// ============================================================================

const fadeInUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const fadeInLeftVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0 },
};

const fadeInRightVariants = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0 },
};

const scaleInVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 },
};

// ============================================================================
// 3. CUSTOM HOOKS
// ============================================================================

export const useIntersectionObserver = (
  config: AnimationConfig = {}
): [React.RefObject<HTMLDivElement>, boolean] => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const {
    threshold = 0.1,
    rootMargin = "0px",
    triggerOnce = true,
  } = config;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isVisible];
};

// ============================================================================
// 4. ANIMATED COMPONENTS (SIMPLIFIED FOR PERFORMANCE)
// ============================================================================

export const FadeInUp = memo<AnimatedElementProps>(
  ({ children, className = "", config = {} }) => {
    const [ref, isVisible] = useIntersectionObserver(config);

    return (
      <div
        ref={ref}
        className={cn("w-full", className)}
      >
        {children}
      </div>
    );
  }
);

export const FadeInLeft = memo<AnimatedElementProps>(
  ({ children, className = "", config = {} }) => {
    const [ref, isVisible] = useIntersectionObserver(config);

    return (
      <div
        ref={ref}
        className={cn("w-full", className)}
      >
        {children}
      </div>
    );
  }
);

export const FadeInRight = memo<AnimatedElementProps>(
  ({ children, className = "", config = {} }) => {
    const [ref, isVisible] = useIntersectionObserver(config);

    return (
      <div
        ref={ref}
        className={cn("w-full", className)}
      >
        {children}
      </div>
    );
  }
);

export const ScaleIn = memo<AnimatedElementProps>(
  ({ children, className = "", config = {} }) => {
    const [ref, isVisible] = useIntersectionObserver(config);

    return (
      <div
        ref={ref}
        className={cn("w-full", className)}
      >
        {children}
      </div>
    );
  }
);

// ============================================================================
// 5. STAGGERED ANIMATION CONTAINER
// ============================================================================

interface StaggerContainerProps {
  children: React.ReactNode;
  className?: string;
  staggerDelay?: number;
  config?: AnimationConfig;
}

export const StaggerContainer = memo<StaggerContainerProps>(
  ({ 
    children, 
    className = "", 
    staggerDelay = 0.1, 
    config = {} 
  }) => {
    const [ref, isVisible] = useIntersectionObserver(config);

    return (
      <div
        ref={ref}
        className={cn("w-full", className)}
      >
        {children}
      </div>
    );
  }
);

// Additional exports for compatibility with existing components
export const AnimatedSection = memo<AnimatedElementProps & { id?: string; animation?: string; delay?: number }>(
  ({ children, className = "", config = {}, id, animation, delay }) => {
    const [ref, isVisible] = useIntersectionObserver(config);

    return (
      <div
        ref={ref}
        id={id}
        className={cn("w-full", className)}
      >
        {children}
      </div>
    );
  }
);

export const AnimatedCard = memo<AnimatedElementProps & { 
  id?: string; 
  animation?: string; 
  delay?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
}>(
  ({ children, className = "", config = {}, id, animation, delay, onMouseEnter, onMouseLeave, onClick }) => {
    const [ref, isVisible] = useIntersectionObserver(config);

    return (
      <div
        ref={ref}
        id={id}
        className={cn("w-full", className)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        {children}
      </div>
    );
  }
);

// ============================================================================
// 6. EXPORTS
// ============================================================================

export {
  fadeInUpVariants,
  fadeInLeftVariants,
  fadeInRightVariants,
  scaleInVariants,
};

export default {
  FadeInUp,
  FadeInLeft,
  FadeInRight,
  ScaleIn,
  StaggerContainer,
  useIntersectionObserver,
};